import React, { useState, useRef, useEffect, useCallback } from 'react';
import L from 'leaflet';
import { useMap } from '../../context/MapContext';
import { cartoTileUrl } from '../../config/carto';
import './MapToolbar.css';

function parseWmsUrl(rawUrl) {
  let baseUrl = rawUrl.trim();
  let queryString = '';
  const qIdx = baseUrl.indexOf('?');
  if (qIdx !== -1) {
    queryString = baseUrl.slice(qIdx + 1);
    baseUrl = baseUrl.slice(0, qIdx);
  }
  const p = new URLSearchParams(queryString);
  return {
    baseUrl,
    layers:      p.get('layers') || p.get('LAYERS') || p.get('layer') || p.get('LAYER') || '',
    format:      p.get('format') || p.get('FORMAT') || 'image/png',
    transparent: (p.get('transparent') || p.get('TRANSPARENT') || 'true').toLowerCase() !== 'false',
    version:     p.get('version') || p.get('VERSION') || '1.1.1',
  };
}

const BASE_LAYERS_LIST = [
  'CartoDB Light',
  'CartoDB Dark',
  'OpenStreetMap',
  'Esri Satellite',
  'Esri Topo',
  'OpenTopoMap',
  'Stadia Dark',
  'IBGE',
  'Sem camada',
];

function IconLayers() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </svg>
  );
}

function IconAddLayer() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 12 12 17 22 12" />
      <line x1="12" y1="19" x2="12" y2="23" />
      <line x1="10" y1="21" x2="14" y2="21" />
    </svg>
  );
}

function IconLabels() {
  return (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="4 7 4 4 20 4 20 7"/>
      <line x1="9" y1="20" x2="15" y2="20"/>
      <line x1="12" y1="4"  x2="12" y2="20"/>
    </svg>
  );
}

function IconLocate() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {/* Mira / crosshair — ícone de localização atual */}
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2"  x2="12" y2="6"  />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="2"  y1="12" x2="6"  y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
    </svg>
  );
}

function MapToolbar({ mapRef, baseLayersRef, activeBaseLayerRef }) {
  const { addWmsLayer, theme } = useMap();

  const [layerPanelOpen, setLayerPanelOpen]   = useState(false);
  const [addPanelOpen,   setAddPanelOpen]      = useState(false);
  const [activeBase,     setActiveBase]        = useState('CartoDB Light');
  const [locating,  setLocating]  = useState(false);
  const [labelsOn,  setLabelsOn]  = useState(false);
  const [wmsUrl,         setWmsUrl]            = useState('');
  const [wmsName,        setWmsName]           = useState('');
  const [addError,       setAddError]          = useState('');

  const labelsRef = useRef(null);

  const layerPanelRef = useRef(null);
  const addPanelRef   = useRef(null);
  // keep old ref name for compat
  const panelRef = layerPanelRef;

  // Sync label layer when theme changes while labels are on
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !labelsOn) return;
    if (labelsRef.current) map.removeLayer(labelsRef.current);
    const url = theme === 'dark'
      ? cartoTileUrl('dark_only_labels')
      : cartoTileUrl('light_only_labels');
    labelsRef.current = L.tileLayer(url, { maxZoom: 20, zIndex: 650, attribution: '&copy; CartoDB' });
    labelsRef.current.addTo(map);
  }, [theme]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleToggleLabels() {
    const map = mapRef.current;
    if (!map) return;
    if (labelsOn) {
      if (labelsRef.current) { map.removeLayer(labelsRef.current); labelsRef.current = null; }
      setLabelsOn(false);
    } else {
      const url = theme === 'dark'
        ? cartoTileUrl('dark_only_labels')
        : cartoTileUrl('light_only_labels');
      labelsRef.current = L.tileLayer(url, { maxZoom: 20, zIndex: 650, attribution: '&copy; CartoDB' });
      labelsRef.current.addTo(map);
      setLabelsOn(true);
    }
  }

  // Close panels on outside click
  useEffect(() => {
    function handleOutside(e) {
      if (layerPanelOpen && layerPanelRef.current && !layerPanelRef.current.contains(e.target)) {
        setLayerPanelOpen(false);
      }
      if (addPanelOpen && addPanelRef.current && !addPanelRef.current.contains(e.target)) {
        setAddPanelOpen(false);
      }
    }
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [layerPanelOpen, addPanelOpen]);

  const handleAddLayer = useCallback(() => {
    setAddError('');
    if (!wmsUrl.trim()) { setAddError('Informe a URL WMS.'); return; }
    let parsed;
    try { parsed = parseWmsUrl(wmsUrl); } catch { setAddError('URL inválida.'); return; }
    if (!parsed.baseUrl.startsWith('http')) { setAddError('URL deve começar com http:// ou https://'); return; }
    addWmsLayer({
      name: wmsName.trim() || parsed.layers || 'Camada WMS',
      baseUrl: parsed.baseUrl,
      layers: parsed.layers,
      format: parsed.format,
      transparent: parsed.transparent,
      version: parsed.version,
    });
    setWmsUrl('');
    setWmsName('');
    setAddPanelOpen(false);
  }, [wmsUrl, wmsName, addWmsLayer]);

  function handleLocate() {
    const map = mapRef.current;
    if (!map) return;
    setLocating(true);
    map.locate({ setView: true, maxZoom: 14 });
    map.once('locationfound', () => setLocating(false));
    map.once('locationerror', () => {
      setLocating(false);
      alert('Não foi possível obter sua localização.');
    });
  }

  function handleBaseLayerChange(name) {
    const map = mapRef.current;
    const baseLayers = baseLayersRef.current;
    if (!map || !baseLayers) return;

    // Remove current base
    Object.values(baseLayers).forEach((l) => {
      if (map.hasLayer(l)) map.removeLayer(l);
    });

    // Add selected base
    baseLayers[name].addTo(map);
    baseLayers[name].bringToBack();
    activeBaseLayerRef.current = baseLayers[name];
    setActiveBase(name);
    setLayerPanelOpen(false);
  }

  return (
    <div className="map-toolbar">
      {/* Adicionar camada WMS externa */}
      <div className="map-toolbar-layer-wrap" ref={addPanelRef}>
        <button
          className={`map-toolbar-btn${addPanelOpen ? ' active' : ''}`}
          onClick={() => { setAddPanelOpen((v) => !v); setLayerPanelOpen(false); }}
          title="Adicionar camada WMS externa"
        >
          <IconAddLayer />
        </button>

        {addPanelOpen && (
          <div className="map-toolbar-layer-panel map-toolbar-add-panel">
            <div className="map-toolbar-layer-title">Adicionar Camada WMS</div>
            <textarea
              className="toolbar-wms-url"
              placeholder="Cole a URL WMS aqui…"
              value={wmsUrl}
              onChange={(e) => setWmsUrl(e.target.value)}
              rows={3}
            />
            <input
              className="toolbar-wms-name"
              type="text"
              placeholder="Nome (opcional)"
              value={wmsName}
              onChange={(e) => setWmsName(e.target.value)}
            />
            {addError && <p className="toolbar-wms-error">{addError}</p>}
            <button className="toolbar-wms-add" onClick={handleAddLayer}>
              + Adicionar
            </button>
          </div>
        )}
      </div>

      {/* Layer switcher */}
      <div className="map-toolbar-layer-wrap" ref={layerPanelRef}>
        <button
          className={`map-toolbar-btn${layerPanelOpen ? ' active' : ''}`}
          onClick={() => setLayerPanelOpen((v) => !v)}
          title="Selecionar camada base"
        >
          <IconLayers />
        </button>

        {layerPanelOpen && (
          <div className="map-toolbar-layer-panel">
            <div className="map-toolbar-layer-title">Camada Base</div>
            {BASE_LAYERS_LIST.map((name) => (
              <label key={name} className="map-toolbar-layer-option">
                <input
                  type="radio"
                  name="base-layer"
                  checked={activeBase === name}
                  onChange={() => handleBaseLayerChange(name)}
                />
                <span>{name}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="map-toolbar-divider" />

      {/* Toponímia */}
      <button
        className={`map-toolbar-btn${labelsOn ? ' active' : ''}`}
        onClick={handleToggleLabels}
        title={labelsOn ? 'Ocultar toponímia' : 'Mostrar toponímia'}
      >
        <IconLabels />
      </button>

      <div className="map-toolbar-divider" />

      {/* Geolocate */}
      <button
        className={`map-toolbar-btn${locating ? ' locating' : ''}`}
        onClick={handleLocate}
        title="Minha localização"
        disabled={locating}
      >
        <IconLocate />
      </button>
    </div>
  );
}

export default MapToolbar;
